// The Available action: Request, or the durable mark that a request already exists.
//
// Pending is not a button. Declined is named, and Request stays so they can ask again.
//
// "Why this account?" opens on its own row under the account (`RequestReasonRow`), spanning the
// table, not inside this cell: an input and two buttons in the last column widened it until the
// bank table needed a horizontal scroll (Vikram's test pass, 7 Oct). The row that owns both
// cells keeps the open state (`AvailableTable`'s `BankRow`).

import { useState } from 'react';
import type { BankRequest, BookAccount } from '../api/types';
import { declinedForViewer, isApprovedRequest, isPendingRequest, requestMark } from './bankRequest.ts';
import { strategistLabel } from './queueSignal.ts';
import {
  MAX_REQUEST_REASON_LENGTH, REQUEST_REASON_LABEL, REQUEST_REASON_PLACEHOLDER, requestReason,
} from './requestReason.ts';

// A strategist's display name from their address; the bank row has no list to hand.
function labelForEmail(email: string): string {
  return strategistLabel(email, []);
}

/** Whether this row shows a Request button (so its reason row may open). */
export function canRequestRow(canClaim: boolean, request: BankRequest | null, row: BookAccount): boolean {
  if (isPendingRequest(request) || isApprovedRequest(request)) return false;
  if (row.competitor) return false;
  return canClaim;
}

export function ClaimCell({
  canClaim,
  busy,
  request,
  viewerEmail,
  row,
  reasonOpen,
  onOpenReason,
}: {
  canClaim: boolean;
  busy: boolean;
  request: BankRequest | null;
  viewerEmail: string;
  row: BookAccount;
  reasonOpen: boolean;
  onOpenReason: () => void;
}) {
  if (isPendingRequest(request) && request) {
    const mark = requestMark(request, viewerEmail, labelForEmail);
    return (
      <span className="requestmark" title={mark.title}>
        {mark.label}
        {mark.who ? <span className="requestwho">{mark.who}</span> : null}
      </span>
    );
  }

  if (isApprovedRequest(request)) {
    return (
      <span className="requestmark" title="Approved">
        Approved ✓
      </span>
    );
  }

  // A competitor marked in Salesforce is never requested; the server refuses it anyway (GRO-314).
  if (row.competitor) {
    return (
      <span className="requestmark" title="Marked as a competitor in Salesforce">
        Competitor
      </span>
    );
  }

  if (!canClaim) return null;

  const declined = declinedForViewer(request, viewerEmail);
  const reason = declined?.declined_reason || null;

  return (
    <div className="claimcell">
      {declined ? (
        <span className="requestdeclined" title={reason ? `Declined — ${reason}` : 'Declined'}>
          Declined
        </span>
      ) : null}
      <button
        type="button"
        className="btn"
        aria-expanded={reasonOpen}
        disabled={busy || !row.domain || reasonOpen}
        onClick={onOpenReason}
      >
        {busy ? 'Requesting…' : declined ? 'Request again' : 'Request'}
      </button>
    </div>
  );
}

// "Why this account?" and Send, on a row of its own under the account, across every column:
// a request always carries the strategist's reason.
export function RequestReasonRow({
  columnCount, busy, accountName, onSend, onCancel,
}: {
  columnCount: number;
  busy: boolean;
  accountName: string;
  onSend: (why: string) => void;
  onCancel: () => void;
}) {
  const [why, setWhy] = useState('');
  const reason = requestReason(why);

  return (
    <tr className="acct requestreasonrow">
      <td colSpan={columnCount}>
        <form
          className="requestreason"
          onSubmit={event => {
            event.preventDefault();
            if (reason) onSend(reason);
          }}
        >
          <input
            type="text"
            aria-label={`${REQUEST_REASON_LABEL} (${accountName})`}
            placeholder={`${REQUEST_REASON_LABEL} ${REQUEST_REASON_PLACEHOLDER}`}
            maxLength={MAX_REQUEST_REASON_LENGTH}
            value={why}
            disabled={busy}
            autoFocus
            onChange={event => setWhy(event.target.value)}
          />
          <button type="submit" className="btn primary" disabled={busy || !reason}>
            {busy ? 'Requesting…' : 'Send'}
          </button>
          <button type="button" className="btn" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        </form>
      </td>
    </tr>
  );
}
